import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, AlertTitle } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { API_ENDPOINTS } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import RetailOsApp, { init as initRetailOs } from './retailos/RetailOsApp';
import './retailos/RetailOS.css';
import './retailos/RetailOS.ezdocs.css';

// The ported RetailOS dashboard. This shell does the two things the standalone
// HTML did with embedded <script type="application/json"> blocks: fetch the
// retail snapshot and the promotion metadata, hand them to the app's init(),
// then mount it. The dashboard's own stylesheet is scoped under .retailos-root
// so nothing in it can reach the rest of ezdocs.
//
// plans/retailos-integration.md: account sales are live from the warehouse;
// promotion evidence is the handoff snapshot; and (phase 2) the workspace —
// tasks, drafts, reviews, notes, targets, the ad import — is the team's, in
// ezdocs' database, with per-user watchlist and saved views. A workspace
// backup exported from the standalone tool still imports through the same
// Restore dialog; it now replaces the team's records.
const RetailOS = () => {
  const theme = useTheme();
  const [state, setState] = useState({ status: 'loading', error: null, source: null });

  useEffect(() => {
    let live = true;
    const headers = { Authorization: 'Bearer ' + localStorage.getItem('jwtToken') };
    (async () => {
      try {
        const [s, m, w] = await Promise.all([
          fetch(API_ENDPOINTS.RETAILOS_SNAPSHOT, { headers }),
          fetch(API_ENDPOINTS.RETAILOS_PROMOTION_META, { headers }),
          fetch(API_ENDPOINTS.RETAILOS_WORKSPACE, { headers }),
        ]);
        for (const r of [s, m, w]) {
          if (!r.ok) {
            let detail = '';
            try { detail = (await r.json()).error || ''; } catch (e) { /* not JSON */ }
            throw new Error(detail || `RetailOS data request failed (${r.status})`);
          }
        }
        const [snapshot, meta, workspace] = await Promise.all([s.json(), m.json(), w.json()]);
        if (!live) return;
        initRetailOs(snapshot, meta, workspace);
        setState({ status: 'ready', error: null, source: snapshot._source || null });
      } catch (e) {
        if (live) setState({ status: 'error', error: e.message, source: null });
      }
    })();
    return () => { live = false; };
  }, []);

  if (state.status === 'loading') {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Opening RetailOS…</Typography>
      </Box>
    );
  }

  if (state.status === 'error') {
    return (
      <Box sx={{ p: 3, maxWidth: 720, margin: '0 auto' }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Retry</Button>}>
          <AlertTitle>RetailOS could not load</AlertTitle>
          {state.error}
        </Alert>
      </Box>
    );
  }

  // The panel is position:fixed below the AppBar (RetailOS.ezdocs.css), so
  // anything that should be visible has to live inside it.
  return (
    <div className="retailos-root">
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <FavoriteButton interfaceName="RetailOS" category="reports" />
      </Box>
      {state.source && state.source.mode !== 'live' && (
        <Alert severity="warning" sx={{ m: 2 }}>
          Showing the frozen {state.source.asOf} snapshot — {state.source.note}.
        </Alert>
      )}
      <RetailOsApp />
    </div>
  );
};

export default RetailOS;
